import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export type SavedJobsSort = 'newest' | 'oldest' | 'title';

export interface SavedJobsQuery {
  q?: string;
  source?: string;
  sort?: SavedJobsSort;
  limit?: number;
  offset?: number;
}

interface SaveJobResult {
  jobId: number;
  created: boolean;
}

export interface SavedJobEntry {
  id: number;
  saved_at: string;
  title: string;
  company: string | null;
  location: string | null;
  rate: string | null;
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string | null;
  pay_provenance?: string | null;
  source: string;
  source_url: string | null;
  job_type: string | null;
  roles_needed: string[];
  start_date: string | null;
  apply_method: string | null;
  activity_status?: string | null;
  activity_checked_at?: string | null;
}

interface SavedJobsResponse {
  jobs: SavedJobEntry[];
  total: number;
  matchingTotal?: number;
  hasMore?: boolean;
  sources?: string[];
  ownedTotal?: number;
  unavailableTotal?: number;
}

export interface SavedJobsState {
  jobs: SavedJobEntry[];
  savedIds: number[];
  total: number;
  matchingTotal: number;
  ownedTotal: number;
  unavailableTotal: number;
  sources: string[];
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  idsLoading: boolean;
  error: string | null;
  loadMoreError: string | null;
  currentListRequestId: string | null;
  currentLoadMoreRequestId: string | null;
  currentIdsRequestId: string | null;
  pendingUnsaveRequestIds: string[];
}

const initialState: SavedJobsState = {
  jobs: [],
  savedIds: [],
  total: 0,
  matchingTotal: 0,
  ownedTotal: 0,
  unavailableTotal: 0,
  sources: [],
  hasMore: false,
  loading: false,
  loadingMore: false,
  idsLoading: false,
  error: null,
  loadMoreError: null,
  currentListRequestId: null,
  currentLoadMoreRequestId: null,
  currentIdsRequestId: null,
  pendingUnsaveRequestIds: [],
};

export const fetchSavedJobs = createAsyncThunk<SavedJobsResponse, SavedJobsQuery | undefined>(
  'savedJobs/fetchSavedJobs',
  async (params = {}) => {
    const { data } = await api.get('/saved-jobs', { params });
    return data;
  },
);

export const loadMoreSavedJobs = createAsyncThunk<SavedJobsResponse, SavedJobsQuery>(
  'savedJobs/loadMoreSavedJobs',
  async (params) => {
    const { data } = await api.get('/saved-jobs', { params });
    return data;
  },
);

export const fetchSavedIds = createAsyncThunk('savedJobs/fetchSavedIds', async () => {
  const { data } = await api.get('/saved-jobs/ids');
  return data.ids as number[];
});

export const saveJob = createAsyncThunk<SaveJobResult, number>(
  'savedJobs/saveJob',
  async (jobId) => {
    const response = await api.post(`/saved-jobs/${jobId}`);
    return {
      jobId,
      created: response.status === 201,
    };
  },
);

export const unsaveJob = createAsyncThunk('savedJobs/unsaveJob', async (jobId: number) => {
  await api.delete(`/saved-jobs/${jobId}`);
  return jobId;
});

const savedJobsSlice = createSlice({
  name: 'savedJobs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSavedJobs.pending, (state, action) => {
        if ((state.pendingUnsaveRequestIds?.length ?? 0) > 0) return;

        state.loading = true;
        state.loadingMore = false;
        state.error = null;
        state.loadMoreError = null;
        state.currentListRequestId = action.meta.requestId;
        state.currentLoadMoreRequestId = null;
      })
      .addCase(fetchSavedJobs.fulfilled, (state, action) => {
        if (state.currentListRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.jobs = action.payload.jobs;
        state.total = action.payload.total;
        state.matchingTotal = action.payload.matchingTotal ?? action.payload.total;
        state.ownedTotal = action.payload.ownedTotal ?? action.payload.total;
        state.unavailableTotal = action.payload.unavailableTotal ?? 0;
        state.hasMore = action.payload.hasMore
          ?? state.jobs.length < state.matchingTotal;
        state.sources = action.payload.sources ?? [];
        state.currentListRequestId = null;
      })
      .addCase(fetchSavedJobs.rejected, (state, action) => {
        if (state.currentListRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.error = action.error.message || 'Failed to load saved jobs';
        state.currentListRequestId = null;
      })
      .addCase(loadMoreSavedJobs.pending, (state, action) => {
        if ((state.pendingUnsaveRequestIds?.length ?? 0) > 0) return;

        state.loadingMore = true;
        state.loadMoreError = null;
        state.currentLoadMoreRequestId = action.meta.requestId;
      })
      .addCase(loadMoreSavedJobs.fulfilled, (state, action) => {
        if (state.currentLoadMoreRequestId !== action.meta.requestId) return;

        const loadedIds = new Set(state.jobs.map(job => job.id));
        const nextJobs = action.payload.jobs.filter(job => !loadedIds.has(job.id));
        state.loadingMore = false;
        state.jobs = [...state.jobs, ...nextJobs];
        state.total = action.payload.total;
        state.matchingTotal = action.payload.matchingTotal ?? action.payload.total;
        state.ownedTotal = action.payload.ownedTotal ?? state.ownedTotal;
        state.unavailableTotal = action.payload.unavailableTotal ?? state.unavailableTotal;
        state.hasMore = action.payload.hasMore
          ?? state.jobs.length < state.matchingTotal;
        state.sources = action.payload.sources ?? state.sources;
        state.currentLoadMoreRequestId = null;
      })
      .addCase(loadMoreSavedJobs.rejected, (state, action) => {
        if (state.currentLoadMoreRequestId !== action.meta.requestId) return;

        state.loadingMore = false;
        state.loadMoreError = action.error.message || 'Failed to load more saved jobs';
        state.currentLoadMoreRequestId = null;
      })
      .addCase(fetchSavedIds.pending, (state, action) => {
        if ((state.pendingUnsaveRequestIds?.length ?? 0) > 0) return;

        state.idsLoading = true;
        state.currentIdsRequestId = action.meta.requestId;
      })
      .addCase(fetchSavedIds.fulfilled, (state, action) => {
        if (state.currentIdsRequestId !== action.meta.requestId) return;

        state.idsLoading = false;
        state.savedIds = action.payload;
        state.currentIdsRequestId = null;
      })
      .addCase(fetchSavedIds.rejected, (state, action) => {
        if (state.currentIdsRequestId !== action.meta.requestId) return;

        state.idsLoading = false;
        state.currentIdsRequestId = null;
      })
      .addCase(saveJob.fulfilled, (state, action) => {
        // Any list/ID snapshot that started before this POST can omit the new
        // bookmark. Invalidate it before applying the successful mutation.
        state.currentListRequestId = null;
        state.currentLoadMoreRequestId = null;
        state.currentIdsRequestId = null;
        state.loading = false;
        state.loadingMore = false;
        state.idsLoading = false;
        if (!state.savedIds.includes(action.payload.jobId)) {
          state.savedIds.push(action.payload.jobId);
        }
        if (action.payload.created) {
          state.total += 1;
          state.ownedTotal = (state.ownedTotal ?? 0) + 1;
        }
      })
      .addCase(unsaveJob.pending, (state, action) => {
        // A list response that started before this DELETE may still contain the
        // job being removed. Invalidate both list request types so that stale
        // response cannot restore the row or overwrite the decremented totals.
        const pendingRequestIds = state.pendingUnsaveRequestIds ?? [];
        if (!pendingRequestIds.includes(action.meta.requestId)) {
          state.pendingUnsaveRequestIds = [
            ...pendingRequestIds,
            action.meta.requestId,
          ];
        }
        state.currentListRequestId = null;
        state.currentLoadMoreRequestId = null;
        state.currentIdsRequestId = null;
        state.loading = false;
        state.loadingMore = false;
        state.idsLoading = false;
      })
      .addCase(unsaveJob.fulfilled, (state, action) => {
        const requestId = action.meta?.requestId;
        const pendingRequestIds = state.pendingUnsaveRequestIds ?? [];
        if (requestId && !pendingRequestIds.includes(requestId)) return;

        const removedFromCurrentList = state.jobs.some(job => job.id === action.payload);
        state.savedIds = state.savedIds.filter(id => id !== action.payload);
        state.jobs = state.jobs.filter(j => j.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        state.ownedTotal = Math.max(0, (state.ownedTotal ?? state.total + 1) - 1);
        if (removedFromCurrentList) {
          state.matchingTotal = Math.max(0, state.matchingTotal - 1);
          state.hasMore = state.jobs.length < state.matchingTotal;
        }
        state.pendingUnsaveRequestIds = requestId
          ? pendingRequestIds.filter(id => id !== requestId)
          : pendingRequestIds;
      })
      .addCase(unsaveJob.rejected, (state, action) => {
        const requestId = action.meta?.requestId;
        const pendingRequestIds = state.pendingUnsaveRequestIds ?? [];
        if (requestId && !pendingRequestIds.includes(requestId)) return;

        state.pendingUnsaveRequestIds = requestId
          ? pendingRequestIds.filter(id => id !== requestId)
          : pendingRequestIds;
      });
  },
});

export default savedJobsSlice.reducer;
